import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Alert, MenuItem, Select,
    FormControl, InputLabel, Chip, Grid, Checkbox, FormGroup
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Lock as LockIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import api from '../services/api';
import { clearPermissionsCache } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

const RECURSOS = [
        { valor: 'cliente', label: 'Cliente' },
        { valor: 'processo', label: 'Processo' },
        { valor: 'andamento', label: 'Andamento' },
        { valor: 'advogado', label: 'Advogado' },
        { valor: 'prazo', label: 'Prazo' },
        { valor: 'auditoria', label: 'Auditoria' },
        { valor: 'usuario', label: 'Usuário' },
        { valor: 'fluxo_caixa', label: 'Fluxo de Caixa' }
    ];

// Ações padrão (aplicadas a todos os recursos exceto fluxo_caixa)
const ACÕES_PADRAO = [
    { valor: 'create', label: 'Criar' },
    { valor: 'read', label: 'Ler' },
    { valor: 'update', label: 'Atualizar' },
    { valor: 'delete', label: 'Deletar' }
];

// Ações específicas para fluxo_caixa
const ACÕES_FLUXO_CAIXA = [
    { valor: 'view', label: 'Visualizar (Básico)' },
    { valor: 'read', label: 'Ler Lançamentos' },
    { valor: 'statistics', label: 'Ver Estatísticas' },
    { valor: 'reports', label: 'Ver Relatórios' },
    { valor: 'create', label: 'Criar' },
    { valor: 'update', label: 'Atualizar' },
    { valor: 'delete', label: 'Deletar' }
];

// Função helper para obter ações baseado no recurso
const getAcoes = (recurso) => {
    if (recurso === 'fluxo_caixa') {
        return ACÕES_FLUXO_CAIXA;
    }
    return ACÕES_PADRAO;
};

function Usuarios() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openPermissoesDialog, setOpenPermissoesDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ 
        nome: '', 
        username: '', 
        password: '', 
        email: '', 
        role: 'USER', 
        ativo: true 
    });
    const [permissoes, setPermissoes] = useState({});
    const [error, setError] = useState('');

    useEffect(() => {
        loadUsuarios();
    }, []);

    const loadUsuarios = async () => {
        setLoading(true);
        try {
            const response = await api.get('/usuarios');
            setUsuarios(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao carregar usuários';
            setError(errorMessage);
            alert('Erro ao carregar usuários: ' + errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const loadPermissoes = async (usuarioId) => {
        try {
            const response = await api.get(`/usuarios/${usuarioId}/permissoes`);
            const perms = {};
            if (response.data && Array.isArray(response.data)) {
                response.data.forEach(p => {
                    const key = `${p.recurso}_${p.acao}`;
                    perms[key] = p.permitido === 1;
                });
            }
            setPermissoes(perms);
        } catch (error) {
            console.error('Erro ao carregar permissões:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao carregar permissões';
            alert('Erro ao carregar permissões: ' + errorMessage);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ 
                nome: item.nome || '', 
                username: item.username || '', 
                password: '', 
                email: item.email || '', 
                role: item.role || 'USER', 
                ativo: item.ativo === 1 
            });
        } else {
            setEditingItem(null);
            setFormData({ 
                nome: '', 
                username: '', 
                password: '', 
                email: '', 
                role: 'USER', 
                ativo: true 
            });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleOpenPermissoesDialog = async (item) => {
        setEditingItem(item);
        await loadPermissoes(item.id);
        setOpenPermissoesDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ 
            nome: '', 
            username: '', 
            password: '', 
            email: '', 
            role: 'USER', 
            ativo: true 
        });
        setError('');
    };

    const handleClosePermissoesDialog = () => {
        // Limpa o estado e fecha o dialog
        setOpenPermissoesDialog(false);
        setPermissoes({});
        setEditingItem(null);
    };

    const handleSave = async () => {
        if (!formData.nome.trim() || !formData.username.trim()) {
            setError('Nome e username são obrigatórios');
            return;
        }
        
        if (!editingItem && !formData.password.trim()) {
            setError('Senha é obrigatória para novos usuários');
            return;
        }

        try {
            const dataToSend = { ...formData };
            if (!dataToSend.password || dataToSend.password.trim() === '') {
                delete dataToSend.password; // Remove senha vazia na edição
            }
            
            if (editingItem) {
                await api.put(`/usuarios/${editingItem.id}`, dataToSend);
            } else {
                await api.post('/usuarios', dataToSend);
            }
            
            // Fecha o dialog primeiro
            handleCloseDialog();
            
            // Aguarda o dialog fechar completamente antes de recarregar
            setTimeout(() => {
                loadUsuarios();
            }, 300);
        } catch (error) {
            console.error('[USUARIOS] Erro ao salvar usuário:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao salvar usuário';
            setError(errorMessage);
            alert('Erro ao salvar usuário: ' + errorMessage);
        }
    };

    const handleSavePermissoes = async () => {
        try {
            const permissoesArray = [];
            RECURSOS.forEach(recurso => {
                const acoes = getAcoes(recurso.valor);
                acoes.forEach(acao => {
                    const key = `${recurso.valor}_${acao.valor}`;
                    permissoesArray.push({
                        recurso: recurso.valor,
                        acao: acao.valor,
                        permitido: permissoes[key] || false
                    });
                });
            });
            
            await api.put(`/usuarios/${editingItem.id}/permissoes`, { permissoes: permissoesArray });
            
            // Limpa o cache de permissões para que o usuário veja as mudanças imediatamente
            clearPermissionsCache();
            
            // Fecha o dialog primeiro
            setOpenPermissoesDialog(false);
            setPermissoes({});
            const usuarioId = editingItem.id;
            setEditingItem(null);
            
            // Aguarda o dialog fechar completamente antes de recarregar
            setTimeout(() => {
                loadUsuarios();
            }, 300);
            
            alert('Permissões atualizadas com sucesso! Recomenda-se recarregar a página (F5) para ver as mudanças.');
        } catch (error) {
            console.error('Erro ao salvar permissões:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao salvar permissões';
            setError(errorMessage);
            alert('Erro ao salvar permissões: ' + errorMessage);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
            try {
                await api.delete(`/usuarios/${id}`);
                loadUsuarios();
            } catch (error) {
                console.error('Erro ao deletar usuário:', error);
                alert('Erro ao deletar usuário: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const handlePermissaoChange = (recurso, acao, checked) => {
        const key = `${recurso}_${acao}`;
        setPermissoes({ ...permissoes, [key]: checked });
    };

    const usuariosFiltrados = usuarios.filter(u => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            u.nome?.toLowerCase().includes(searchLower) ||
            u.username?.toLowerCase().includes(searchLower) ||
            u.email?.toLowerCase().includes(searchLower)
        );
    });

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se a data já vem formatada do backend (YYYY-MM-DD HH:mm:ss), formata diretamente
            // O backend agora retorna em UTC, então precisamos tratar como UTC
            if (typeof date === 'string') {
                // Formato: "2026-01-07 10:16:05" (UTC)
                const match = date.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                if (match) {
                    const [, ano, mes, dia, hora, minuto, segundo] = match;
                    // Cria uma data em UTC e converte para o timezone local (America/Sao_Paulo)
                    const dataUTC = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}Z`);
                    // Formata para o timezone local
                    const diaLocal = String(dataUTC.getDate()).padStart(2, '0');
                    const mesLocal = String(dataUTC.getMonth() + 1).padStart(2, '0');
                    const anoLocal = dataUTC.getFullYear();
                    const horaLocal = String(dataUTC.getHours()).padStart(2, '0');
                    const minutoLocal = String(dataUTC.getMinutes()).padStart(2, '0');
                    const segundoLocal = String(dataUTC.getSeconds()).padStart(2, '0');
                    return `${diaLocal}/${mesLocal}/${anoLocal}, ${horaLocal}:${minutoLocal}:${segundoLocal}`;
                }
                // Se já está em formato brasileiro, retorna como está
                if (date.includes('/')) {
                    return date;
                }
            }
            
            // Fallback: tenta converter (assume UTC se não tiver timezone)
            const d = new Date(date);
            if (isNaN(d.getTime())) {
                return date;
            }
            
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const ano = d.getFullYear();
            const hora = String(d.getHours()).padStart(2, '0');
            const minuto = String(d.getMinutes()).padStart(2, '0');
            const segundo = String(d.getSeconds()).padStart(2, '0');
            
            return `${dia}/${mes}/${ano}, ${hora}:${minuto}:${segundo}`;
        } catch {
            return date;
        }
    };

    return (
        <Container>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">
                    Gerenciar Usuários
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Novo Usuário
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por nome, username ou email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell align="center">Ativo</TableCell>
                            <TableCell>Último Acesso</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : usuariosFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {search ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            usuariosFiltrados.map((usuario) => (
                                <TableRow key={usuario.id} hover>
                                    <TableCell>{usuario.nome}</TableCell>
                                    <TableCell>{usuario.username}</TableCell>
                                    <TableCell>{usuario.email || '-'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={usuario.role} 
                                            color={usuario.role === 'ADMIN' ? 'error' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {usuario.ativo === 1 ? '✓' : '✗'}
                                    </TableCell>
                                    <TableCell>{formatDate(usuario.ultimo_acesso)}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate(`/configuracao-usuario/${usuario.id}`)}
                                            color="primary"
                                            title="Configurações de Horário e Inatividade"
                                        >
                                            <SettingsIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenPermissoesDialog(usuario)}
                                            color="primary"
                                            title="Gerenciar Permissões"
                                        >
                                            <LockIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDialog(usuario)}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(usuario.id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog para criar/editar usuário */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Usuário' : 'Novo Usuário'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome"
                                    value={formData.nome}
                                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Username"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Senha"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required={!editingItem}
                                    helperText={editingItem ? 'Deixe em branco para manter a senha atual' : ''}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.role}
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        label="Role"
                                    >
                                        <MenuItem value="USER">Usuário</MenuItem>
                                        <MenuItem value="ADMIN">Administrador</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.ativo}
                                            onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                        />
                                    }
                                    label="Ativo"
                                />
                            </Grid>
                        </Grid>
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para gerenciar permissões */}
            <Dialog open={openPermissoesDialog} onClose={handleClosePermissoesDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Gerenciar Permissões - {editingItem?.nome}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Selecione as permissões para este usuário:
                        </Typography>
                        <Grid container spacing={2}>
                            {RECURSOS.map((recurso) => {
                                const acoes = getAcoes(recurso.valor);
                                return (
                                    <Grid item xs={12} key={recurso.valor}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {recurso.label}
                                            </Typography>
                                            <FormGroup>
                                                {acoes.map((acao) => {
                                                    const key = `${recurso.valor}_${acao.valor}`;
                                                    return (
                                                        <FormControlLabel
                                                            key={key}
                                                            control={
                                                                <Checkbox
                                                                    checked={permissoes[key] || false}
                                                                    onChange={(e) => handlePermissaoChange(recurso.valor, acao.valor, e.target.checked)}
                                                                />
                                                            }
                                                            label={acao.label}
                                                        />
                                                    );
                                                })}
                                            </FormGroup>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePermissoesDialog}>Cancelar</Button>
                    <Button onClick={handleSavePermissoes} variant="contained">
                        Salvar Permissões
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default Usuarios;

